import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, Trash2, Briefcase, Plane, Building2, Clock, CheckCircle2, Flag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { careerApi } from '@/services/careerApi';
import CareerStageForm from './CareerStageForm';
import type { CareerStage, CareerStageRequest } from '@/types/api.types';

export default function CareerPage() {
  const { toast } = useToast();
  const [stages, setStages] = useState<CareerStage[]>([]);
  const [currentStage, setCurrentStage] = useState<CareerStage | null>(null);
  const [completedCount, setCompletedCount] = useState(0);
  const [historyAircraftTypes, setHistoryAircraftTypes] = useState<string[]>([]);
  const [historyOperatingUnits, setHistoryOperatingUnits] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingStage, setEditingStage] = useState<CareerStage | null>(null);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, summaryRes, historyRes] = await Promise.all([
        careerApi.getStages(),
        careerApi.getSummary(),
        careerApi.getHistory(),
      ]);
      setStages(stagesRes.data.results || []);
      if (summaryRes.data.success) {
        const data = summaryRes.data.data;
        setCurrentStage(data.currentStage);
        setCompletedCount(data.completedCount);
      }
      if (historyRes.data.success) {
        const data = historyRes.data.data;
        setHistoryAircraftTypes(data.aircraftTypes);
        setHistoryOperatingUnits(data.operatingUnits);
      }
    } catch {
      toast({ title: '加载数据失败', variant: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleCreate = async (payload: CareerStageRequest) => {
    setSaving(true);
    try {
      await careerApi.createStage(payload);
      toast({ title: '阶段添加成功', variant: 'success' });
      setShowForm(false);
      fetchData();
    } catch {
      toast({ title: '添加失败', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (payload: CareerStageRequest) => {
    if (!editingStage) return;
    setSaving(true);
    try {
      await careerApi.updateStage(editingStage.id, payload);
      toast({ title: '修改成功', variant: 'success' });
      setEditingStage(null);
      fetchData();
    } catch {
      toast({ title: '修改失败', variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      await careerApi.deleteStage(id);
      toast({ title: '删除成功', variant: 'success' });
      fetchData();
    } catch {
      toast({ title: '删除失败', variant: 'error' });
    } finally {
      setDeletingId(null);
    }
  };

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  const durationText = (stage: CareerStage) => {
    const start = new Date(stage.startDate);
    const end = stage.endDate ? new Date(stage.endDate) : new Date();
    const months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;
    if (years > 0 && remainingMonths > 0) return `${years}年${remainingMonths}个月`;
    if (years > 0) return `${years}年`;
    if (months > 0) return `${months}个月`;
    return '不足1个月';
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">职业生涯</h1>
          <p className="text-sm text-gray-500 mt-1">管理个人职业发展阶段与运行记录</p>
        </div>
        {!showForm && !editingStage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            添加阶段
          </Button>
        )}
      </div>

      {/* Summary Cards */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="overflow-hidden">
              <CardContent className="p-4 flex items-center gap-4 animate-pulse">
                <div className="w-11 h-11 rounded-xl bg-gray-200 shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-16 rounded bg-gray-100" />
                  <div className="h-5 w-24 rounded bg-gray-200" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <Card className={`overflow-hidden ${currentStage ? 'border-blue-200' : ''}`}>
            <CardContent className="p-4 flex items-center gap-4 bg-gradient-to-br from-blue-50/80 to-transparent">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 shadow-sm shadow-blue-200 flex items-center justify-center shrink-0">
                <Briefcase className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">当前阶段</p>
                <p className="text-lg font-semibold text-gray-900 truncate">
                  {currentStage ? currentStage.title : '无'}
                </p>
                {currentStage?.aircraftType && (
                  <p className="text-xs text-gray-500 mt-0.5">{currentStage.aircraftType}</p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardContent className="p-4 flex items-center gap-4 bg-gradient-to-br from-emerald-50/80 to-transparent">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-600 shadow-sm shadow-emerald-200 flex items-center justify-center shrink-0">
                <CheckCircle2 className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">已结束阶段</p>
                <p className="text-lg font-semibold text-gray-900">{completedCount} 个</p>
              </div>
            </CardContent>
          </Card>

          <Card className="overflow-hidden">
            <CardContent className="p-4 flex items-center gap-4 bg-gradient-to-br from-violet-50/80 to-transparent">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-violet-500 to-violet-600 shadow-sm shadow-violet-200 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <p className="text-xs text-gray-500">总阶段数</p>
                <p className="text-lg font-semibold text-gray-900">{stages.length} 个</p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Add Form */}
      {showForm && (
        <div className="mb-8">
          <CareerStageForm
            onSubmit={handleCreate}
            onCancel={() => setShowForm(false)}
            isLoading={saving}
            historyAircraftTypes={historyAircraftTypes}
            historyOperatingUnits={historyOperatingUnits}
          />
        </div>
      )}

      {/* Timeline */}
      {loading ? (
        /* Skeleton timeline: mirrors the real rail + card layout to avoid layout shift */
        <div className="space-y-6 animate-pulse">
          {[0, 1, 2].map((i) => (
            <div key={i} className="relative flex gap-3 md:gap-5">
              <div className="relative w-8 shrink-0 flex justify-center">
                {i < 2 && <div className="absolute top-0 bottom-[-1.5rem] w-px bg-gray-100" />}
                <div className="relative z-10 mt-2 w-3.5 h-3.5 rounded-full bg-gray-200" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="bg-white rounded-2xl border border-gray-100 p-4 md:p-5 shadow-sm">
                  <div className="h-5 w-40 rounded bg-gray-200" />
                  <div className="h-3.5 w-56 rounded bg-gray-100 mt-2.5" />
                  <div className="flex gap-2 mt-3">
                    <div className="h-6 w-16 rounded-lg bg-gray-100" />
                    <div className="h-6 w-20 rounded-lg bg-gray-100" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : stages.length === 0 ? (
        <div className="text-center py-16 px-6 rounded-2xl border-2 border-dashed border-gray-200 bg-gray-50/50">
          <div className="w-16 h-16 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
            <Briefcase className="w-8 h-8 text-blue-400" />
          </div>
          <p className="font-medium text-gray-700">暂无职业阶段记录</p>
          <p className="text-sm text-gray-400 mt-1 mb-5">记录你的职业发展历程，从第一个阶段开始</p>
          <Button onClick={() => setShowForm(true)} disabled={showForm || !!editingStage}>
            <Plus className="w-4 h-4 mr-2" />
            添加第一个阶段
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {stages.map((stage, index) => {
            const isCurrent = !stage.endDate && !stage.isMilestone;
            const isEditing = editingStage?.id === stage.id;
            const isLast = index === stages.length - 1;

            return (
              <div key={stage.id} className="relative flex gap-3 md:gap-5">
                {/* Rail: fixed width, dot + line always centered on the same axis */}
                <div className="relative w-8 shrink-0 flex justify-center">
                  {/* Line segment: runs through the row and across the gap to the next dot */}
                  {!isLast && (
                    <div className="absolute top-0 bottom-[-1.5rem] w-px bg-gradient-to-b from-gray-200 via-gray-200 to-gray-100" />
                  )}
                  {/* Last item: line ends at the dot */}
                  {isLast && <div className="absolute top-0 h-3 w-px bg-gray-200" />}

                  {stage.isMilestone ? (
                    <div className="relative z-10 mt-1 w-7 h-7 rounded-full bg-amber-50 border border-amber-300 shadow-sm shadow-amber-100 flex items-center justify-center">
                      <Flag className="w-3.5 h-3.5 text-amber-500" />
                    </div>
                  ) : isCurrent ? (
                    <div className="relative z-10 mt-2 flex items-center justify-center">
                      <span className="absolute inline-flex w-5 h-5 rounded-full bg-blue-400/40 animate-ping" />
                      <span className="relative w-3.5 h-3.5 rounded-full bg-blue-500 ring-4 ring-blue-100" />
                    </div>
                  ) : (
                    <div className="relative z-10 mt-2 w-3.5 h-3.5 rounded-full bg-white border-2 border-gray-300" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0 pb-0">
                  {isEditing ? (
                    <CareerStageForm
                      initialData={editingStage}
                      onSubmit={handleUpdate}
                      onCancel={() => setEditingStage(null)}
                      isLoading={saving}
                      historyAircraftTypes={historyAircraftTypes}
                      historyOperatingUnits={historyOperatingUnits}
                    />
                  ) : (
                    <div
                      className={`group bg-white rounded-2xl border p-4 md:p-5 shadow-sm transition-all hover:shadow-md ${
                        isCurrent
                          ? 'border-blue-200 ring-1 ring-blue-100'
                          : stage.isMilestone
                            ? 'border-amber-200 bg-gradient-to-br from-amber-50/60 to-white'
                            : 'border-gray-100'
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="font-semibold text-gray-900">{stage.title}</h3>
                            {stage.stageType && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                {stage.stageType}
                              </span>
                            )}
                            {stage.isMilestone && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                                <Flag className="w-3 h-3 mr-0.5" />
                                里程碑
                              </span>
                            )}
                            {isCurrent && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-50 text-blue-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                                当前阶段
                              </span>
                            )}
                          </div>

                          <p className="text-sm text-gray-500 mt-1.5">
                            {stage.isMilestone ? (
                              <span className="inline-flex items-center gap-1.5">
                                <Flag className="w-3 h-3 text-amber-400" />
                                {formatDate(stage.startDate)}
                              </span>
                            ) : (
                              <>
                                {formatDate(stage.startDate)} ~ {stage.endDate ? formatDate(stage.endDate) : '至今'}
                                <span className="mx-2 text-gray-300">|</span>
                                <span className="text-gray-400">{durationText(stage)}</span>
                              </>
                            )}
                          </p>

                          {(stage.aircraftType || stage.operatingUnit) && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              {stage.aircraftType && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-xs text-gray-600">
                                  <Plane className="w-3.5 h-3.5 text-blue-500" />
                                  {stage.aircraftType}
                                </span>
                              )}
                              {stage.operatingUnit && (
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-100 text-xs text-gray-600">
                                  <Building2 className="w-3.5 h-3.5 text-emerald-500" />
                                  {stage.operatingUnit}
                                </span>
                              )}
                            </div>
                          )}

                          {stage.description && (
                            <p className="text-sm text-gray-500 mt-2.5 leading-relaxed whitespace-pre-line">
                              {stage.description}
                            </p>
                          )}
                        </div>

                        <div className="flex items-center gap-1 shrink-0 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
                          <button
                            onClick={() => setEditingStage(stage)}
                            className="p-1.5 rounded-md text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
                            title="编辑"
                          >
                            <Pencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(stage.id)}
                            disabled={deletingId === stage.id}
                            className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors disabled:opacity-50"
                            title="删除"
                          >
                            {deletingId === stage.id ? (
                              <span className="w-4 h-4 block border-2 border-red-300 border-t-red-600 rounded-full animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
